import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, Send, Loader2, CheckCircle, MapPin, Copy } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReviewForm({ token, onClose }) {
  const { t } = useTranslation();
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [text, setText] = useState('');
  const [destination, setDestination] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/reviews/verify/${token}`);
        setTokenData(res.data);
        setDestination(res.data.product_name || '');
      } catch {
        setInvalid(true);
      }
      setLoading(false);
    };
    verify();
  }, [token]);

  const handleSubmit = async () => {
    if (!rating || text.trim().length < 5) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews/submit`, {
        token, rating, text: text.trim(), destination,
      });
      setCouponCode(res.data.coupon_code || '');
      setSubmitted(true);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#0f1419] border border-white/10 rounded-2xl p-8 max-w-md text-center">
          <p className="text-slate-400 mb-4">{t('review.invalidLink') || 'This review link is invalid or has already been used.'}</p>
          <button onClick={onClose} className="text-teal-400 hover:text-teal-300">{t('common.close') || 'Close'}</button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-[#0f1419] border border-teal-500/30 rounded-2xl p-8 max-w-md text-center"
          data-testid="review-success"
        >
          <CheckCircle className="w-16 h-16 text-teal-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">{t('review.thankYou') || 'Thank you!'}</h2>
          <p className="text-slate-400 mb-6">{t('review.published') || 'Your review has been published and will help other travelers.'}</p>

          {couponCode && (
            <div className="bg-gradient-to-br from-teal-500/10 to-white/5 border-2 border-dashed border-teal-500/40 rounded-xl p-5 mb-6" data-testid="coupon-reward">
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-2">{t('review.yourReward') || 'Your Reward'}</p>
              <p className="text-amber-400 text-sm font-medium mb-3">10% OFF {t('review.nextPurchase') || 'your next purchase'}</p>
              <div className="flex items-center justify-center gap-2">
                <code className="bg-black/40 text-teal-400 text-lg font-bold font-mono px-4 py-2 rounded-lg tracking-wider" data-testid="coupon-code">
                  {couponCode}
                </code>
                <button
                  onClick={handleCopy}
                  className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 p-2 rounded-lg transition-all"
                  data-testid="copy-coupon"
                >
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-slate-500 text-xs mt-3">{t('review.couponNote') || 'Valid for 30 days, single use. Enter at checkout.'}</p>
            </div>
          )}

          <button onClick={onClose} className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-8 py-3 rounded-full transition-all" data-testid="review-continue">
            {t('review.continueBrowsing') || 'Continue Browsing'}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="review-modal">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-[#0f1419] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-white text-xl font-bold mb-1">{t('review.title') || 'Share Your Experience'}</h2>
        <p className="text-slate-400 text-sm mb-6">
          {t('review.subtitle') || 'Help fellow travelers by sharing your eSIM experience'}
        </p>

        {/* Product */}
        {tokenData?.product_name && (
          <div className="bg-white/5 rounded-xl p-3 mb-6 flex items-center gap-3">
            <MapPin className="w-5 h-5 text-teal-400 flex-shrink-0" />
            <span className="text-white text-sm font-medium">{tokenData.product_name}</span>
          </div>
        )}

        {/* Star Rating */}
        <div className="mb-6">
          <label className="text-slate-300 text-sm font-medium block mb-3">{t('review.ratingLabel') || 'Rating'}</label>
          <div className="flex gap-2 justify-center" data-testid="star-rating">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                className="transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  className={`w-9 h-9 ${
                    star <= (hoveredStar || rating)
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-slate-600'
                  } transition-colors`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-amber-400 text-sm mt-2">
              {rating === 5 ? (t('review.rating5') || 'Amazing!') :
               rating === 4 ? (t('review.rating4') || 'Great!') :
               rating === 3 ? (t('review.rating3') || 'Good') :
               rating === 2 ? (t('review.rating2') || 'Fair') :
               (t('review.rating1') || 'Poor')}
            </p>
          )}
        </div>

        {/* Review Text */}
        <div className="mb-6">
          <label className="text-slate-300 text-sm font-medium block mb-2">{t('review.textLabel') || 'Your review'}</label>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('review.placeholder') || 'How was your eSIM experience? Connection quality, speed, ease of setup...'}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500/50 resize-none h-28"
            data-testid="review-text"
          />
          <p className="text-slate-600 text-xs mt-1">{text.length}/500</p>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 py-3 rounded-xl text-sm transition-all"
          >
            {t('review.maybeLater') || 'Maybe Later'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!rating || text.trim().length < 5 || submitting}
            className="flex-1 bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-black font-semibold py-3 rounded-xl text-sm transition-all flex items-center justify-center gap-2"
            data-testid="submit-review"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('review.submit') || 'Submit Review'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
